import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { format, formatDistanceToNow } from "date-fns";
import { useAppBranding } from "@/contexts/app-branding";
import { 
  UserPlus, 
  Shield, 
  ShieldCheck, 
  ShieldOff,
  KeyRound,
  Loader2, 
  RotateCw, 
  RotateCcw,
  X,
  Mail,
  Pencil,
  Copy,
  Check,
  Link,
  Users as UsersIcon,
  Trash2,
  LayoutDashboard,
  Flag,
  Plus,
} from "lucide-react";
import { TOUR_MILESTONE_KEYS } from "@shared/tours/milestone-keys";
import { getRoleLabel } from "@shared/role-labels";
import { RoleVisibilityDialog } from "@/components/role-visibility-dialog";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth-context";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { usePageTitle } from "@/hooks/use-page-title";

interface UserRecord {
  id: number;
  email: string;
  username: string;
  role: string;
  isSuperAdmin: boolean;
  isActive: boolean;
  totpEnabled: boolean;
  createdAt: string;
  lastLoginAt: string | null;
}

interface UsersResponse {
  users: UserRecord[];
}

interface InviteRecord {
  id: string;
  email: string;
  role: string;
  status: "pending" | "accepted" | "expired" | "cancelled";
  invitedBy: { id: string; email: string | null } | null;
  createdAt: string;
  expiresAt: string;
  usedAt: string | null;
  cancelledAt: string | null;
  emailSent: boolean;
}

interface CreateInviteResponse {
  id: string;
  email: string;
  role: string;
  expiresAt: string;
  createdAt: string;
  emailSent: boolean;
  inviteLink?: string;
}

interface InvitesResponse {
  invites: InviteRecord[];
}

const inviteFormSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  role: z.enum(["staff", "staff-coordinator", "admin", "ops"], {
    required_error: "Select a role for this invite.",
    invalid_type_error: "Select a role for this invite.",
  }),
  sendEmail: z.boolean().default(true),
});

type InviteFormData = z.infer<typeof inviteFormSchema>;

const editUserFormSchema = z.object({
  role: z.enum(["staff", "staff-coordinator", "admin", "ops"]),
  isActive: z.boolean(),
});

type EditUserFormData = z.infer<typeof editUserFormSchema>;

function formatDate(dateString: string): string {
  return format(new Date(dateString), "MMM d, yyyy");
}

function formatDateTime(dateString: string): string {
  return format(new Date(dateString), "MMM d, yyyy, h:mm a");
}

function formatExpiryDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  if (date < now) {
    return "Expired";
  }
  return formatDistanceToNow(date, { addSuffix: true });
}

function isUserAdmin(role: string): boolean {
  return role === "admin";
}

export default function Users() {
  usePageTitle("Users");
  const { user: currentUser, isLoading: isAuthLoading } = useAuth();
  const { toast } = useToast();
  const { appName } = useAppBranding();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<UserRecord | null>(null);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [copyingLinkId, setCopyingLinkId] = useState<string | null>(null);
  const [copiedLink, setCopiedLink] = useState<string | null>(null);
  const [lastCreatedInviteLink, setLastCreatedInviteLink] = useState<string | null>(null);
  const [fallbackLinkDialog, setFallbackLinkDialog] = useState<string | null>(null);
  const [superAdminDialog, setSuperAdminDialog] = useState<{ user: UserRecord; enable: boolean } | null>(null);
  const [superAdminPassword, setSuperAdminPassword] = useState("");
  const [superAdminError, setSuperAdminError] = useState<string | null>(null);
  const [resetMfaUser, setResetMfaUser] = useState<UserRecord | null>(null);
  const [resetMfaPassword, setResetMfaPassword] = useState("");
  const [resetMfaError, setResetMfaError] = useState<string | null>(null);
  const [disableMfaUser, setDisableMfaUser] = useState<UserRecord | null>(null);
  const [disableMfaPassword, setDisableMfaPassword] = useState("");
  const [disableMfaError, setDisableMfaError] = useState<string | null>(null);
  const [deleteUserTarget, setDeleteUserTarget] = useState<UserRecord | null>(null);
  const [isVisibilityDialogOpen, setIsVisibilityDialogOpen] = useState(false);

  const [submitting, setSubmitting] = useState(false);

  const form = useForm<InviteFormData>({
    resolver: zodResolver(inviteFormSchema),
    defaultValues: {
      email: "",
      role: undefined,
      sendEmail: true,
    },
  });

  useEffect(() => {
    if (isDrawerOpen) {
      form.reset();
      setLastCreatedInviteLink(null);
    }
  }, [isDrawerOpen]);

  const editForm = useForm<EditUserFormData>({
    resolver: zodResolver(editUserFormSchema),
    defaultValues: {
      role: "staff",
      isActive: true,
    },
  });

  const { data: usersData, isLoading: isUsersLoading, isError: isUsersError, error: usersError } = useQuery<UsersResponse>({
    queryKey: ["/api/users"],
    enabled: !!currentUser?.isAdmin,
  });

  const { data: invitesData, isLoading: isInvitesLoading } = useQuery<InvitesResponse>({
    queryKey: ["/api/invites"],
    enabled: !!currentUser?.isAdmin,
  });

  const createInviteMutation = useMutation({
    mutationFn: async (data: InviteFormData): Promise<CreateInviteResponse> => {
      return await apiRequest("POST", "/api/invites", data);
    },
    onSuccess: (response: CreateInviteResponse, variables: InviteFormData) => {
      if (!variables.sendEmail && response.inviteLink) {
        navigator.clipboard.writeText(response.inviteLink).then(() => {
          toast({
            title: "Invite link copied!",
            description: "The invite link has been created and copied to your clipboard.",
          });
          setSubmitting(false);
          form.reset();
          setIsDrawerOpen(false);
        }).catch(() => {
          setLastCreatedInviteLink(response.inviteLink ?? null);
          toast({
            title: "Invite created",
            description: "Could not auto-copy. Please copy the link manually.",
          });
          setSubmitting(false);
          form.reset();
          // Drawer intentionally left open — admin must copy the link manually.
          // Reset-on-open (useEffect) guarantees a clean form on next open.
        });
      } else {
        toast({
          title: response.emailSent ? "Invite sent" : "Invite created",
          description: response.emailSent 
            ? "The invitation has been sent successfully."
            : "Invite created but email could not be sent. You can resend it later.",
        });
        setSubmitting(false);
        form.reset();
        setIsDrawerOpen(false);
      }
      queryClient.invalidateQueries({ queryKey: ["/api/invites"] });
    },
    onError: (error: Error) => {
      setSubmitting(false);
      const message = error.message || "Failed to send invite";
      if (message.includes("already exists")) {
        form.setError("email", { message: "A user with this email already exists" });
      } else {
        toast({
          title: "Error",
          description: message,
          variant: "destructive",
        });
      }
    },
  });

  const resendInviteMutation = useMutation({
    mutationFn: async (inviteId: string) => {
      setResendingId(inviteId);
      return await apiRequest("POST", `/api/invites/${inviteId}/resend`);
    },
    onSuccess: () => {
      toast({
        title: "Invite resent",
        description: "The invitation has been resent.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/invites"] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to resend invite",
        variant: "destructive",
      });
    },
    onSettled: () => {
      setResendingId(null);
    },
  });

  const cancelInviteMutation = useMutation({
    mutationFn: async (inviteId: string) => {
      setCancellingId(inviteId);
      await apiRequest("DELETE", `/api/invites/${inviteId}`);
    },
    onSuccess: () => {
      toast({
        title: "Invite cancelled",
        description: "The invitation has been cancelled.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/invites"] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to cancel invite",
        variant: "destructive",
      });
    },
    onSettled: () => {
      setCancellingId(null);
    },
  });

  const copyInviteLink = async (inviteId: string) => {
    setCopyingLinkId(inviteId);
    try {
      const response = await apiRequest("GET", `/api/invites/${inviteId}/link`) as { inviteLink: string };
      const link = response.inviteLink;
      
      // Try modern clipboard API first, then fallback to execCommand
      let copied = false;
      try {
        await navigator.clipboard.writeText(link);
        copied = true;
      } catch {
        // Fallback for Safari and other browsers with strict clipboard policies
        const textArea = document.createElement('textarea');
        textArea.value = link;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        textArea.style.top = '-9999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        try {
          copied = document.execCommand('copy');
        } catch {
          copied = false;
        }
        document.body.removeChild(textArea);
      }
      
      if (copied) {
        setCopiedLink(inviteId);
        toast({
          title: "Link copied!",
          description: "The invite link has been copied to your clipboard.",
        });
        setTimeout(() => setCopiedLink(null), 2000);
      } else {
        // Last resort: show dialog with selectable link for manual copy
        setFallbackLinkDialog(link);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to get link",
        variant: "destructive",
      });
    } finally {
      setCopyingLinkId(null);
    }
  };

  const updateUserMutation = useMutation({
    mutationFn: async ({ userId, data }: { userId: number; data: { role?: "staff" | "staff-coordinator" | "admin" | "ops"; isActive?: boolean } }) => {
      return await apiRequest("PATCH", `/api/users/${userId}`, data);
    },
    onSuccess: () => {
      toast({
        title: "User updated",
        description: "The user has been updated successfully.",
      });
      editForm.reset({ role: "staff", isActive: true });
      setEditingUser(null);
      setIsEditDrawerOpen(false);
      queryClient.invalidateQueries({ queryKey: ["/api/users"] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update user",
        variant: "destructive",
      });
    },
  });

  const superAdminMutation = useMutation({
    mutationFn: async ({ userId, isSuperAdmin, password }: { userId: number; isSuperAdmin: boolean; password: string }) => {
      return await apiRequest("POST", `/api/users/${userId}/super-admin`, { isSuperAdmin, password });
    },
    onError: (error: Error) => {
      setSuperAdminError(error.message || "Failed to update super admin status");
    },
  });

  const handleSuperAdminToggle = (user: UserRecord) => {
    setSuperAdminDialog({ user, enable: !user.isSuperAdmin });
    setSuperAdminPassword("");
    setSuperAdminError(null);
  };

  const confirmSuperAdminToggle = async () => {
    if (!superAdminDialog || !superAdminPassword) return;
    const { enable } = superAdminDialog;
    try {
      await superAdminMutation.mutateAsync({
        userId: superAdminDialog.user.id,
        isSuperAdmin: enable,
        password: superAdminPassword,
      });
      toast({
        title: "Super admin updated",
        description: enable
          ? "User has been granted super admin privileges."
          : "Super admin privileges have been revoked.",
      });
      setEditingUser((prev) => {
        if (!prev) return prev;
        const updated = { ...prev, isSuperAdmin: enable };
        if (enable && prev.role !== "admin") {
          updated.role = "admin";
        }
        return updated;
      });
      setSuperAdminDialog(null);
      setSuperAdminPassword("");
      setSuperAdminError(null);
      queryClient.invalidateQueries({ queryKey: ["/api/users"] });
    } catch {
    }
  };

  const resetMfaMutation = useMutation({
    mutationFn: async ({ userId, password }: { userId: number; password: string }) => {
      return await apiRequest("POST", `/api/admin/users/${userId}/reset-totp`, { password });
    },
    onSuccess: () => {
      toast({
        title: "MFA reset",
        description: `Two-factor authentication has been reset for ${resetMfaUser?.email}. They must re-enroll on next sign-in.`,
      });
      if (resetMfaUser) {
        setEditingUser((prev) =>
          prev && prev.id === resetMfaUser.id ? { ...prev, totpEnabled: false } : prev
        );
      }
      setResetMfaUser(null);
      setResetMfaPassword("");
      setResetMfaError(null);
      queryClient.invalidateQueries({ queryKey: ["/api/users"] });
    },
    onError: (error: Error) => {
      setResetMfaError(error.message || "Failed to reset MFA");
    },
  });

  const confirmResetMfa = () => {
    if (!resetMfaUser || !resetMfaPassword) return;
    resetMfaMutation.mutate({
      userId: resetMfaUser.id,
      password: resetMfaPassword,
    });
  };

  const disableMfaMutation = useMutation({
    mutationFn: async ({ userId, password }: { userId: number; password: string }) => {
      return await apiRequest("POST", `/api/admin/users/${userId}/disable-totp`, { password });
    },
    onSuccess: () => {
      toast({
        title: "MFA disabled",
        description: `Two-factor authentication has been disabled for ${disableMfaUser?.email}.`,
      });
      if (disableMfaUser) {
        setEditingUser((prev) =>
          prev && prev.id === disableMfaUser.id ? { ...prev, totpEnabled: false } : prev
        );
      }
      setDisableMfaUser(null);
      setDisableMfaPassword("");
      setDisableMfaError(null);
      queryClient.invalidateQueries({ queryKey: ["/api/users"] });
    },
    onError: (error: Error) => {
      setDisableMfaError(error.message || "Failed to disable MFA");
    },
  });

  const confirmDisableMfa = () => {
    if (!disableMfaUser || !disableMfaPassword) return;
    disableMfaMutation.mutate({
      userId: disableMfaUser.id,
      password: disableMfaPassword,
    });
  };

  const deleteUserMutation = useMutation({
    mutationFn: async (userId: number) => {
      return await apiRequest("DELETE", `/api/users/${userId}`);
    },
    onSuccess: () => {
      toast({
        title: "User deleted",
        description: `${deleteUserTarget?.email} has been permanently deleted.`,
      });
      setDeleteUserTarget(null);
      setEditingUser(null);
      setIsEditDrawerOpen(false);
      queryClient.invalidateQueries({ queryKey: ["/api/users"] });
      queryClient.invalidateQueries({ queryKey: ["/api/invites"] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete user",
        variant: "destructive",
      });
    },
  });

  // P23C: milestone management — query + grant/revoke mutations for the edit drawer.
  interface MilestoneRecord {
    key: string;
    achievedAt: string | null;
    source: string;
    revokedAt: string | null;
  }

  const { data: milestonesData, isLoading: isMilestonesLoading } = useQuery<{ milestones: MilestoneRecord[] }>({
    queryKey: ["/api/admin/users", editingUser?.id, "milestones"],
    enabled: !!editingUser && !!currentUser?.isSuperAdmin && isEditDrawerOpen,
  });

  const grantMilestoneMutation = useMutation({
    mutationFn: async ({ userId, key }: { userId: number; key: string }) =>
      apiRequest("POST", `/api/admin/users/${userId}/milestones`, { key }),
    onSuccess: (_data, variables) => {
      toast({ title: "Milestone granted", description: variables.key });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users", editingUser?.id, "milestones"] });
    },
    onError: (error: Error) =>
      toast({ title: "Failed to grant milestone", description: error.message, variant: "destructive" }),
  });

  const revokeMilestoneMutation = useMutation({
    mutationFn: async ({ userId, key }: { userId: number; key: string }) =>
      apiRequest("DELETE", `/api/admin/users/${userId}/milestones/${key}`),
    onSuccess: (_data, variables) => {
      toast({ title: "Milestone revoked", description: variables.key });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users", editingUser?.id, "milestones"] });
    },
    onError: (error: Error) =>
      toast({ title: "Failed to revoke milestone", description: error.message, variant: "destructive" }),
  });

  const onSubmitInvite = (data: InviteFormData) => {
    setLastCreatedInviteLink(null);
    setSubmitting(true);
    createInviteMutation.mutate(data);
  };

  const handleCopyLink = async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      setCopiedLink(link);
      toast({
        title: "Copied!",
        description: "Invite link copied to clipboard.",
      });
      // Close drawer after successful manual copy
      setIsDrawerOpen(false);
      setTimeout(() => setCopiedLink(null), 2000);
    } catch (error) {
      toast({
        title: "Copy failed",
        description: "Could not copy to clipboard.",
        variant: "destructive",
      });
    }
  };

  const onSubmitEditUser = (data: EditUserFormData) => {
    if (!editingUser) return;
    
    const isSelf = currentUser?.id === String(editingUser.id);
    
    const updates: { role?: "staff" | "staff-coordinator" | "admin" | "ops"; isActive?: boolean } = {};
    
    if (!isSelf) {
      updates.role = data.role as "staff" | "staff-coordinator" | "admin" | "ops";
      updates.isActive = data.isActive;
    }
    
    if (Object.keys(updates).length === 0) {
      toast({
        title: "No changes",
        description: "You cannot modify your own role or status.",
      });
      editForm.reset({ role: "staff", isActive: true });
      setEditingUser(null);
      setIsEditDrawerOpen(false);
      queryClient.invalidateQueries({ queryKey: ["/api/users"] });
      return;
    }
    
    updateUserMutation.mutate({
      userId: editingUser.id,
      data: updates,
    });
  };

  const openEditDrawer = (user: UserRecord) => {
    setEditingUser(user);
    editForm.reset({
      role: user.role as "staff" | "staff-coordinator" | "admin" | "ops",
      isActive: user.isActive,
    });
    setIsEditDrawerOpen(true);
  };

  const isLoading = isAuthLoading || isUsersLoading;

  if (isLoading) {
    return (
      <div className="flex-1 px-4 sm:px-6 pb-6 pt-4">
        <PageHeader
          breadcrumb={[{ label: "Administration" }, { label: "Users" }]}
          title={
            <span className="flex items-center gap-2" data-testid="heading-users">
              <UsersIcon className="w-6 h-6 text-primary" />
              Users
            </span>
          }
          actions={
            <Button disabled data-testid="button-invite-user">
              <UserPlus className="h-4 w-4 mr-2" />
              Invite user
            </Button>
          }
        />

        <p className="text-muted-foreground mb-6">
          Manage who has access to {appName}.
        </p>

        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table className="table-zebra">
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Username</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Last login</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {[1, 2, 3].map((i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton className="h-4 w-40" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-16" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-16" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                    <TableCell className="text-right"><Skeleton className="h-8 w-16 ml-auto" /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!currentUser?.isSuperAdmin) {
    return (
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 pb-6 pt-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8">
            <div className="text-center">
              <Shield className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
              <h2 className="text-xl font-semibold mb-2" data-testid="text-access-denied-title">
                Not Authorized
              </h2>
              <p className="text-muted-foreground mb-6" data-testid="text-access-denied-message">
                This page is only available to authorized personnel. If you need access, please contact your system administrator.
              </p>
              <Button asChild data-testid="button-go-to-dashboard">
                <a href="/dashboard">Go to Dashboard</a>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isUsersError) {
    const errorMessage = usersError instanceof Error ? usersError.message : "Unknown error";
    const is403 = errorMessage.includes("403") || errorMessage.includes("Forbidden");
    
    return (
      <div className="flex-1 px-4 sm:px-6 pb-6 pt-4">
        <PageHeader
          breadcrumb={[{ label: "Administration" }, { label: "Users" }]}
          title={
            <span className="flex items-center gap-2" data-testid="heading-users">
              <UsersIcon className="w-6 h-6 text-primary" />
              Users
            </span>
          }
        />

        <Card>
          <CardHeader>
            <CardTitle>{is403 ? "Not Authorized" : "Error"}</CardTitle>
          </CardHeader>
          <CardContent className="p-8">
            <div className="text-center text-muted-foreground">
              <Shield className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p data-testid="text-error-message">
                {is403 
                  ? "You do not have permission to view this page. Please contact an administrator if you believe this is an error."
                  : "Unable to load users. Please try again."
                }
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const users = usersData?.users || [];
  const pendingInvites = (invitesData?.invites || []).filter(
    (invite) => invite.status === "pending"
  );

  return (
    <div className="flex-1 px-4 sm:px-6 pb-6 pt-4">
      <PageHeader
        breadcrumb={[{ label: "Administration" }, { label: "Users" }]}
        title={
          <span className="flex items-center gap-2" data-testid="heading-users">
            <UsersIcon className="w-6 h-6 text-primary" />
            Users
          </span>
        }
        actions={
          <div className="flex items-center gap-2">
            {currentUser?.isSuperAdmin && (
              <Button variant="outline" onClick={() => setIsVisibilityDialogOpen(true)} data-testid="button-configure-visibility">
                <LayoutDashboard className="h-4 w-4 mr-2" />
                Configure visibility
              </Button>
            )}
            <Button onClick={() => setIsDrawerOpen(true)} data-testid="button-invite-user">
              <UserPlus className="h-4 w-4 mr-2" />
              Invite user
            </Button>
          </div>
        }
      />

      <p className="text-muted-foreground mb-6">
        Manage who has access to {appName}.
      </p>

      <Card className="mb-8">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table className="table-zebra">
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Username</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>MFA</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Last login</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      No users found
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user) => (
                    <TableRow 
                      key={user.id}
                      data-testid={`row-user-${user.id}`}
                    >
                      <TableCell data-testid={`text-email-${user.id}`}>
                        {user.email}
                      </TableCell>
                      <TableCell data-testid={`text-username-${user.id}`}>
                        {user.username}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Badge 
                            variant={isUserAdmin(user.role) ? "default" : "secondary"}
                            data-testid={`badge-role-${user.id}`}
                          >
                            {isUserAdmin(user.role) && <ShieldCheck className="h-3 w-3 mr-1" />}
                            {getRoleLabel(user.role)}
                          </Badge>
                          {user.isSuperAdmin && (
                            <Badge 
                              variant="outline"
                              className="border-amber-500 text-amber-700 dark:text-amber-400"
                              data-testid={`badge-super-admin-${user.id}`}
                            >
                              <Shield className="h-3 w-3 mr-1" />
                              Super Admin
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant={user.isActive ? "outline" : "secondary"}
                          className={user.isActive ? "border-green-500 text-green-700 dark:text-green-400" : ""}
                          data-testid={`badge-status-${user.id}`}
                        >
                          {user.isActive ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={user.totpEnabled ? "outline" : "secondary"}
                          className={user.totpEnabled ? "border-blue-500 text-blue-700 dark:text-blue-400" : ""}
                          data-testid={`badge-mfa-${user.id}`}
                        >
                          <KeyRound className="h-3 w-3 mr-1" />
                          {user.totpEnabled ? "Enabled" : "Disabled"}
                        </Badge>
                      </TableCell>
                      <TableCell 
                        className="text-muted-foreground"
                        data-testid={`text-created-${user.id}`}
                      >
                        {formatDate(user.createdAt)}
                      </TableCell>
                      <TableCell 
                        className="text-muted-foreground"
                        data-testid={`text-last-login-${user.id}`}
                      >
                        {user.lastLoginAt ? formatDateTime(user.lastLoginAt) : "Never"}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2 flex-wrap">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openEditDrawer(user)}
                            data-testid={`button-edit-user-${user.id}`}
                          >
                            <Pencil className="h-4 w-4 mr-1" />
                            Edit
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Pending Invites
          </CardTitle>
          <CardDescription>
            Invitations that have been sent but not yet accepted
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table className="table-zebra">
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead>Invited by</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isInvitesLoading ? (
                  [1, 2].map((i) => (
                    <TableRow key={i}>
                      <TableCell><Skeleton className="h-4 w-40" /></TableCell>
                      <TableCell><Skeleton className="h-5 w-16" /></TableCell>
                      <TableCell><Skeleton className="h-5 w-16" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                      <TableCell><Skeleton className="h-8 w-24 ml-auto" /></TableCell>
                    </TableRow>
                  ))
                ) : pendingInvites.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      No pending invites
                    </TableCell>
                  </TableRow>
                ) : (
                pendingInvites.map((invite) => (
                  <TableRow 
                    key={invite.id}
                    data-testid={`row-invite-${invite.id}`}
                  >
                    <TableCell data-testid={`text-invite-email-${invite.id}`}>
                      {invite.email}
                    </TableCell>
                    <TableCell>
                      <Badge 
                        variant={isUserAdmin(invite.role) ? "default" : "secondary"}
                        data-testid={`badge-invite-role-${invite.id}`}
                      >
                        {isUserAdmin(invite.role) && <ShieldCheck className="h-3 w-3 mr-1" />}
                        {getRoleLabel(invite.role)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge 
                        variant="outline"
                        data-testid={`badge-invite-method-${invite.id}`}
                      >
                        {invite.emailSent ? (
                          <>
                            <Mail className="h-3 w-3 mr-1" />
                            Email
                          </>
                        ) : (
                          <>
                            <Link className="h-3 w-3 mr-1" />
                            Link
                          </>
                        )}
                      </Badge>
                    </TableCell>
                    <TableCell 
                      className="text-muted-foreground"
                      data-testid={`text-invite-expires-${invite.id}`}
                    >
                      {formatExpiryDate(invite.expiresAt)}
                    </TableCell>
                    <TableCell 
                      className="text-muted-foreground"
                      data-testid={`text-invite-inviter-${invite.id}`}
                    >
                      {invite.invitedBy?.email || "Unknown"}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {!invite.emailSent ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => copyInviteLink(invite.id)}
                            disabled={copyingLinkId === invite.id}
                            data-testid={`button-copy-link-${invite.id}`}
                          >
                            {copyingLinkId === invite.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : copiedLink === invite.id ? (
                              <Check className="h-4 w-4 mr-1" />
                            ) : (
                              <Copy className="h-4 w-4 mr-1" />
                            )}
                            {copiedLink === invite.id ? "Copied" : "Copy Link"}
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => resendInviteMutation.mutate(invite.id)}
                            disabled={resendingId === invite.id}
                            data-testid={`button-resend-${invite.id}`}
                          >
                            {resendingId === invite.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <RotateCw className="h-4 w-4 mr-1" />
                            )}
                            Resend
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => cancelInviteMutation.mutate(invite.id)}
                          disabled={cancellingId === invite.id}
                          data-testid={`button-cancel-${invite.id}`}
                        >
                          {cancellingId === invite.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <X className="h-4 w-4 mr-1" />
                          )}
                          Cancel
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Sheet open={isDrawerOpen} onOpenChange={(open) => {
        // Only handles close events (Escape, backdrop click).
        // Open-path reset is owned by the useEffect keyed to isDrawerOpen.
        setIsDrawerOpen(open);
      }}>
        <SheetContent data-testid="drawer-invite-user">
          <SheetHeader>
            <SheetTitle>Invite User</SheetTitle>
            <SheetDescription>
              Send an invitation to a new user to join {appName}.
            </SheetDescription>
          </SheetHeader>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmitInvite)} className="space-y-6 mt-6">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="user@example.com" 
                        type="email"
                        data-testid="input-invite-email"
                        {...field} 
                      />
                    </FormControl>
                    <FormMessage data-testid="error-invite-email" />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="role"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Role</FormLabel>
                    <FormControl>
                      <RadioGroup
                        onValueChange={field.onChange}
                        value={field.value ?? ""}
                        className="flex flex-col space-y-3"
                        data-testid="radio-group-role"
                      >
                        <div className="flex items-center space-x-3">
                          <RadioGroupItem 
                            value="staff" 
                            id="role-staff"
                            data-testid="radio-role-staff"
                          />
                          <Label htmlFor="role-staff" className="font-normal cursor-pointer">
                            <span className="font-medium">Staff Manager</span>
                            <span className="text-muted-foreground ml-2">— Standard access to view and manage animals</span>
                          </Label>
                        </div>
                        <div className="flex items-center space-x-3">
                          <RadioGroupItem 
                            value="staff-coordinator"
                            id="role-staff-coordinator"
                            data-testid="radio-role-staff-coordinator"
                          />
                          <Label htmlFor="role-staff-coordinator" className="font-normal cursor-pointer">
                            <span className="font-medium">Staff Coordinator</span>
                            <span className="text-muted-foreground ml-2">— Staff access with coordination responsibilities</span>
                          </Label>
                        </div>
                        <div className="flex items-center space-x-3">
                          <RadioGroupItem 
                            value="ops"
                            id="role-ops"
                            data-testid="radio-role-ops"
                          />
                          <Label htmlFor="role-ops" className="font-normal cursor-pointer">
                            <span className="font-medium">Operations</span>
                            <span className="text-muted-foreground ml-2">— Standard access to view and manage animals</span>
                          </Label>
                        </div>
                        <div className="flex items-center space-x-3">
                          <RadioGroupItem 
                            value="admin" 
                            id="role-admin"
                            data-testid="radio-role-admin"
                          />
                          <Label htmlFor="role-admin" className="font-normal cursor-pointer">
                            <span className="font-medium">Admin</span>
                            <span className="text-muted-foreground ml-2">— Full access including user management</span>
                          </Label>
                        </div>
                      </RadioGroup>
                    </FormControl>
                    <FormMessage data-testid="error-invite-role" />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="sendEmail"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <FormLabel className="text-sm font-medium">Send email</FormLabel>
                        <div className="text-sm text-muted-foreground">
                          {field.value 
                            ? "Invitation will be emailed to the user."
                            : "Copy the invite link manually instead."}
                        </div>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          data-testid="switch-send-email"
                        />
                      </FormControl>
                    </div>
                  </FormItem>
                )}
              />

              {lastCreatedInviteLink && !form.watch("sendEmail") && (
                <div className="rounded-lg border p-4 bg-muted/50">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium mb-1">Invite Link Created</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {lastCreatedInviteLink}
                      </p>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => handleCopyLink(lastCreatedInviteLink)}
                      data-testid="button-copy-link"
                    >
                      {copiedLink === lastCreatedInviteLink ? (
                        <Check className="h-4 w-4" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              )}

              <Button 
                type="submit" 
                className="w-full"
                disabled={createInviteMutation.isPending || submitting}
                data-testid="button-send-invite"
              >
                {createInviteMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {form.watch("sendEmail") ? "Sending..." : "Creating..."}
                  </>
                ) : form.watch("sendEmail") ? (
                  <>
                    <Mail className="h-4 w-4 mr-2" />
                    Send Invite
                  </>
                ) : (
                  <>
                    <Link className="h-4 w-4 mr-2" />
                    Create Invite Link
                  </>
                )}
              </Button>
            </form>
          </Form>
        </SheetContent>
      </Sheet>

      <Sheet open={isEditDrawerOpen} onOpenChange={setIsEditDrawerOpen}>
        <SheetContent data-testid="drawer-edit-user" className="flex flex-col overflow-hidden">
          <SheetHeader>
            <SheetTitle>Edit User</SheetTitle>
            <SheetDescription>
              Update role and status for this account.
            </SheetDescription>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto">
          {editingUser && (
            <Form {...editForm}>
              <form onSubmit={editForm.handleSubmit(onSubmitEditUser)} className="space-y-6 mt-6">
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Email</Label>
                  <Input 
                    value={editingUser.email}
                    disabled
                    className="bg-muted"
                    data-testid="input-edit-email"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-muted-foreground">Username</Label>
                  <Input 
                    value={editingUser.username}
                    disabled
                    className="bg-muted"
                    data-testid="input-edit-username"
                  />
                </div>

                <FormField
                  control={editForm.control}
                  name="isActive"
                  render={({ field }) => {
                    const isSelf = currentUser?.id === String(editingUser.id);
                    return (
                      <FormItem>
                        <FormLabel>Account Status</FormLabel>
                        <div className="flex items-center justify-between rounded-lg border p-4">
                          <div className="space-y-0.5">
                            <div className="text-sm font-medium">
                              {field.value ? "Active" : "Inactive"}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {isSelf 
                                ? "You cannot deactivate your own account."
                                : field.value 
                                  ? "User can sign in to the system."
                                  : "Inactive users cannot sign in."
                              }
                            </div>
                          </div>
                          <FormControl>
                            <Switch
                              checked={field.value}
                              onCheckedChange={field.onChange}
                              disabled={isSelf}
                              data-testid="switch-edit-status"
                            />
                          </FormControl>
                        </div>
                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />

                <FormField
                  control={editForm.control}
                  name="role"
                  render={({ field }) => {
                    const isSelf = currentUser?.id === String(editingUser.id);
                    const staffDisabledBySA = editingUser.isSuperAdmin === true;
                    return (
                      <FormItem>
                        <FormLabel>Role</FormLabel>
                        <FormControl>
                          <RadioGroup
                            onValueChange={field.onChange}
                            value={field.value}
                            className="flex flex-col space-y-3"
                            disabled={isSelf}
                            data-testid="radio-group-edit-role"
                          >
                            <div className="flex items-center space-x-3">
                              <RadioGroupItem 
                                value="staff" 
                                id="edit-role-staff"
                                disabled={isSelf || staffDisabledBySA}
                                data-testid="radio-edit-role-staff"
                              />
                              <Label htmlFor="edit-role-staff" className={`font-normal ${(isSelf || staffDisabledBySA) ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                                <span className="font-medium">Staff Manager</span>
                                <span className="text-muted-foreground ml-2">— Standard access to view and manage animals</span>
                              </Label>
                            </div>
                            <div className="flex items-center space-x-3">
                              <RadioGroupItem 
                                value="staff-coordinator"
                                id="edit-role-staff-coordinator"
                                disabled={isSelf || staffDisabledBySA}
                                data-testid="radio-edit-role-staff-coordinator"
                              />
                              <Label htmlFor="edit-role-staff-coordinator" className={`font-normal ${(isSelf || staffDisabledBySA) ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                                <span className="font-medium">Staff Coordinator</span>
                                <span className="text-muted-foreground ml-2">— Staff access with coordination responsibilities</span>
                              </Label>
                            </div>
                            <div className="flex items-center space-x-3">
                              <RadioGroupItem 
                                value="ops"
                                id="edit-role-ops"
                                disabled={isSelf || staffDisabledBySA}
                                data-testid="radio-edit-role-ops"
                              />
                              <Label htmlFor="edit-role-ops" className={`font-normal ${(isSelf || staffDisabledBySA) ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                                <span className="font-medium">Operations</span>
                                <span className="text-muted-foreground ml-2">— Standard access to view and manage animals</span>
                              </Label>
                            </div>
                            <div className="flex items-center space-x-3">
                              <RadioGroupItem 
                                value="admin" 
                                id="edit-role-admin"
                                disabled={isSelf}
                                data-testid="radio-edit-role-admin"
                              />
                              <Label htmlFor="edit-role-admin" className={`font-normal ${isSelf ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                                <span className="font-medium">Admin</span>
                                <span className="text-muted-foreground ml-2">— Full access including user management</span>
                              </Label>
                            </div>
                          </RadioGroup>
                        </FormControl>
                        {isSelf && (
                          <p className="text-sm text-muted-foreground">
                            You cannot change your own role.
                          </p>
                        )}
                        {staffDisabledBySA && !isSelf && (
                          <p className="text-sm text-muted-foreground" data-testid="text-staff-disabled-by-sa">
                            Revoke Super Admin access before changing this user's role to Staff Manager.
                          </p>
                        )}
                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />

                {currentUser?.isSuperAdmin && (
                  <div className="space-y-2 pt-4 border-t">
                    <Label className="flex items-center gap-2">
                      <Shield className="h-4 w-4 text-amber-500" />
                      Super Admin
                    </Label>
                    <div className="flex items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <div className="text-sm font-medium">
                          {editingUser.isSuperAdmin ? "Super Admin" : "Not Super Admin"}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {currentUser?.id === String(editingUser.id) 
                            ? "You cannot modify your own super admin status."
                            : editingUser.isSuperAdmin
                              ? "Super admins have access to audit logs and system-level settings."
                              : editingUser.role !== "admin"
                                ? "Super Admin access can only be granted to Admin users."
                                : "Super admins have access to audit logs and system-level settings."
                          }
                        </div>
                      </div>
                      {currentUser?.id !== String(editingUser.id) && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => handleSuperAdminToggle(editingUser)}
                          disabled={!editingUser.isSuperAdmin && editingUser.role !== "admin"}
                          data-testid="button-toggle-super-admin"
                        >
                          {editingUser.isSuperAdmin ? "Revoke" : "Grant"}
                        </Button>
                      )}
                    </div>
                  </div>
                )}

                <div className="space-y-2 pt-4 border-t">
                  <Label className="flex items-center gap-2">
                    <KeyRound className="h-4 w-4" />
                    Two-Factor Authentication
                  </Label>
                  <div className="flex items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <div className="text-sm font-medium flex items-center gap-2">
                        <Badge
                          variant={editingUser.totpEnabled ? "outline" : "secondary"}
                          className={editingUser.totpEnabled ? "border-blue-500 text-blue-700 dark:text-blue-400" : ""}
                          data-testid="badge-drawer-mfa-status"
                        >
                          {editingUser.totpEnabled ? "Enabled" : "Not enrolled"}
                        </Badge>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {editingUser.totpEnabled
                          ? "This user has two-factor authentication enabled."
                          : "This user has not set up two-factor authentication."
                        }
                      </div>
                    </div>
                    {currentUser?.isSuperAdmin && editingUser.totpEnabled && currentUser?.id !== String(editingUser.id) && (
                      <div className="flex gap-2 flex-wrap">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setDisableMfaUser(editingUser);
                            setDisableMfaPassword("");
                            setDisableMfaError(null);
                          }}
                          data-testid="button-drawer-disable-mfa"
                        >
                          <ShieldOff className="h-4 w-4 mr-1" />
                          Disable MFA
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setResetMfaUser(editingUser);
                            setResetMfaPassword("");
                            setResetMfaError(null);
                          }}
                          data-testid="button-drawer-reset-mfa"
                        >
                          <RotateCcw className="h-4 w-4 mr-1" />
                          Reset MFA
                        </Button>
                      </div>
                    )}
                  </div>
                  {currentUser?.isSuperAdmin && currentUser?.id === String(editingUser.id) && editingUser.totpEnabled && (
                    <p className="text-sm text-muted-foreground">
                      You cannot disable or reset your own two-factor authentication from here. Use your Account page instead.
                    </p>
                  )}
                </div>

                {currentUser?.isSuperAdmin && (
                  <div className="space-y-3 pt-4 border-t">
                    <Label className="flex items-center gap-2">
                      <Flag className="h-4 w-4 text-muted-foreground" />
                      Milestones
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Milestones control tour targeting and onboarding state. Changes take effect on the user's next page load.
                    </p>
                    {isMilestonesLoading ? (
                      <p className="text-xs text-muted-foreground">Loading…</p>
                    ) : (
                      <div className="space-y-2">
                        {TOUR_MILESTONE_KEYS.map((key) => {
                          const row = milestonesData?.milestones.find((m) => m.key === key);
                          const active = !!row && !row.revokedAt;
                          return (
                            <div
                              key={key}
                              className="flex items-center justify-between rounded-lg border p-3 gap-2"
                              data-testid={`milestone-row-${key}`}
                            >
                              <div className="min-w-0">
                                <p className="text-sm font-mono font-medium truncate">{key}</p>
                                {active && row?.achievedAt && (
                                  <p className="text-xs text-muted-foreground">
                                    {row.source === "admin" ? "Admin-granted" : "System"} · {format(new Date(row.achievedAt), "MMM d, yyyy")}
                                  </p>
                                )}
                                {!active && (
                                  <p className="text-xs text-muted-foreground">Not granted</p>
                                )}
                              </div>
                              {currentUser?.id !== String(editingUser.id) && (
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    if (active) {
                                      revokeMilestoneMutation.mutate({ userId: editingUser.id, key });
                                    } else {
                                      grantMilestoneMutation.mutate({ userId: editingUser.id, key });
                                    }
                                  }}
                                  disabled={grantMilestoneMutation.isPending || revokeMilestoneMutation.isPending}
                                  data-testid={`button-milestone-toggle-${key}`}
                                >
                                  {active ? (
                                    <>
                                      <X className="h-3 w-3 mr-1" />
                                      Revoke
                                    </>
                                  ) : (
                                    <>
                                      <Plus className="h-3 w-3 mr-1" />
                                      Grant
                                    </>
                                  )}
                                </Button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {currentUser?.isSuperAdmin && currentUser?.id !== String(editingUser.id) && (
                  <div className="space-y-2 pt-4 border-t">
                    <Label className="text-destructive">Danger Zone</Label>
                    <div className="flex items-center justify-between rounded-lg border border-destructive/30 p-4">
                      <div className="space-y-0.5">
                        <div className="text-sm font-medium">Delete User</div>
                        <div className="text-sm text-muted-foreground">
                          Permanently remove this user and unlink all their data.
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={() => setDeleteUserTarget(editingUser)}
                        data-testid="button-delete-user"
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                )}

                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={() => setIsEditDrawerOpen(false)}
                    data-testid="button-cancel-edit"
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    className="flex-1"
                    disabled={updateUserMutation.isPending}
                    data-testid="button-save-user"
                  >
                    {updateUserMutation.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      "Save changes"
                    )}
                  </Button>
                </div>
              </form>
            </Form>
          )}
          </div>
        </SheetContent>
      </Sheet>

      <AlertDialog open={!!fallbackLinkDialog} onOpenChange={(open) => !open && setFallbackLinkDialog(null)}>
        <AlertDialogContent data-testid="dialog-copy-link">
          <AlertDialogHeader>
            <AlertDialogTitle>Copy Invite Link</AlertDialogTitle>
            <AlertDialogDescription>
              Select and copy the link below:
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="rounded-md border bg-muted p-3">
            <Input
              readOnly
              value={fallbackLinkDialog || ""}
              className="font-mono text-sm bg-transparent border-0 p-0 h-auto focus-visible:ring-0"
              onClick={(e) => (e.target as HTMLInputElement).select()}
              data-testid="input-copy-link"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogAction 
              onClick={() => setFallbackLinkDialog(null)}
              data-testid="button-close-copy-dialog"
            >
              Done
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog 
        open={!!superAdminDialog} 
        onOpenChange={(open) => {
          if (!open) {
            setSuperAdminDialog(null);
            setSuperAdminPassword("");
            setSuperAdminError(null);
          }
        }}
      >
        <AlertDialogContent data-testid="dialog-super-admin-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-amber-500" />
              {superAdminDialog?.enable ? "Grant Super Admin Privileges" : "Revoke Super Admin Privileges"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {superAdminDialog?.enable 
                ? `You are about to grant super admin privileges to ${superAdminDialog?.user.email}. This will give them access to audit logs and system-level settings.`
                : `You are about to revoke super admin privileges from ${superAdminDialog?.user.email}.`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="super-admin-password">
                Enter your password to confirm
              </Label>
              <Input
                id="super-admin-password"
                type="password"
                placeholder="Your password"
                value={superAdminPassword}
                onChange={(e) => {
                  setSuperAdminPassword(e.target.value);
                  setSuperAdminError(null);
                }}
                data-testid="input-super-admin-password"
              />
              {superAdminError && (
                <p className="text-sm text-destructive" data-testid="text-super-admin-error">
                  {superAdminError}
                </p>
              )}
            </div>
          </div>
          <AlertDialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setSuperAdminDialog(null);
                setSuperAdminPassword("");
                setSuperAdminError(null);
              }}
              data-testid="button-cancel-super-admin"
            >
              Cancel
            </Button>
            <Button
              onClick={confirmSuperAdminToggle}
              disabled={!superAdminPassword || superAdminMutation.isPending}
              variant={superAdminDialog?.enable ? "default" : "destructive"}
              data-testid="button-confirm-super-admin"
            >
              {superAdminMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                superAdminDialog?.enable ? "Grant Super Admin" : "Revoke Super Admin"
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!resetMfaUser}
        onOpenChange={(open) => {
          if (!open) {
            setResetMfaUser(null);
            setResetMfaPassword("");
            setResetMfaError(null);
          }
        }}
      >
        <AlertDialogContent data-testid="dialog-reset-mfa-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-destructive" />
              Reset Two-Factor Authentication
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will force re-enrollment of two-factor authentication for {resetMfaUser?.email}. Their current setup will be cleared and they must enroll again on next sign-in.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="reset-mfa-password">
                Enter your password to confirm
              </Label>
              <Input
                id="reset-mfa-password"
                type="password"
                placeholder="Your password"
                value={resetMfaPassword}
                onChange={(e) => {
                  setResetMfaPassword(e.target.value);
                  setResetMfaError(null);
                }}
                data-testid="input-reset-mfa-password"
              />
              {resetMfaError && (
                <p className="text-sm text-destructive" data-testid="text-reset-mfa-error">
                  {resetMfaError}
                </p>
              )}
            </div>
          </div>
          <AlertDialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setResetMfaUser(null);
                setResetMfaPassword("");
                setResetMfaError(null);
              }}
              data-testid="button-cancel-reset-mfa"
            >
              Cancel
            </Button>
            <Button
              onClick={confirmResetMfa}
              disabled={!resetMfaPassword || resetMfaMutation.isPending}
              variant="destructive"
              data-testid="button-confirm-reset-mfa"
            >
              {resetMfaMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Resetting...
                </>
              ) : (
                "Reset MFA"
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!disableMfaUser}
        onOpenChange={(open) => {
          if (!open) {
            setDisableMfaUser(null);
            setDisableMfaPassword("");
            setDisableMfaError(null);
          }
        }}
      >
        <AlertDialogContent data-testid="dialog-disable-mfa-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <ShieldOff className="h-5 w-5 text-destructive" />
              Disable Two-Factor Authentication
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will turn off two-factor authentication for {disableMfaUser?.email}. They will no longer need a code to sign in unless enforcement policy requires re-enrollment.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="disable-mfa-password">
                Enter your password to confirm
              </Label>
              <Input
                id="disable-mfa-password"
                type="password"
                placeholder="Your password"
                value={disableMfaPassword}
                onChange={(e) => {
                  setDisableMfaPassword(e.target.value);
                  setDisableMfaError(null);
                }}
                data-testid="input-disable-mfa-password"
              />
              {disableMfaError && (
                <p className="text-sm text-destructive" data-testid="text-disable-mfa-error">
                  {disableMfaError}
                </p>
              )}
            </div>
          </div>
          <AlertDialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setDisableMfaUser(null);
                setDisableMfaPassword("");
                setDisableMfaError(null);
              }}
              data-testid="button-cancel-disable-mfa"
            >
              Cancel
            </Button>
            <Button
              onClick={confirmDisableMfa}
              disabled={!disableMfaPassword || disableMfaMutation.isPending}
              variant="destructive"
              data-testid="button-confirm-disable-mfa"
            >
              {disableMfaMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Disabling...
                </>
              ) : (
                "Disable MFA"
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!deleteUserTarget}
        onOpenChange={(open) => !open && setDeleteUserTarget(null)}
      >
        <AlertDialogContent data-testid="dialog-delete-user">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User Permanently</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <span className="font-semibold">{deleteUserTarget?.email}</span> and
              unlink all their associated data (reports, tasks, activity logs, etc.). This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeleteUserTarget(null)}
              data-testid="button-cancel-delete-user"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteUserTarget && deleteUserMutation.mutate(deleteUserTarget.id)}
              disabled={deleteUserMutation.isPending}
              data-testid="button-confirm-delete-user"
            >
              {deleteUserMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete Permanently"
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <RoleVisibilityDialog
        open={isVisibilityDialogOpen}
        onOpenChange={setIsVisibilityDialogOpen}
      />
    </div>
  );
}
